//@ts-nocheck
'use client'

import React, { useState, useMemo, useEffect } from 'react';
import * as S from './Calendar.styles';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  isToday,
  parseISO 
} from 'date-fns';
import { it, enGB } from 'date-fns/locale';

import { Heading, Paragraph } from '../../atoms/Typography/Typography.component';
import { SpacerBase, Container } from '../../atoms/GridSystem';
import Button from '../../atoms/Button/Button.component';
import Icon from '../../atoms/Icon/Icon.component';
import useMediaQuery from '../../../hooks/useMediaQuery'
import AccordionSub from '../../molecules/Accordion/AccordionSub.component';

import { getPageLocale } from '@caffeina-siad/utils';
import { useTranslations } from 'next-intl';

// Types
export interface CalendarEvent {
  id: string;
  date?: Date;
  endDate?: Date;
  // Supporto per formato alternativo di date
  dateFrom?: string;
  dateTo?: string;
  title: string;
  description?: string;
  link?: string;
  agent?: string;
}

interface CalendarProps {
  events: CalendarEvent[];
  title: HeadingProps;
}

const API = {
  getEvents: (locale: string, site: string, year: number, month: number) => `${process?.env?.NEXT_PUBLIC_API_URL || ''}/api/events/${locale}/${site}/all?year=${year}&month=${month}`,
}

export const Calendar: React.FC<CalendarProps> = ({ 
  title = { content: 'Calendario Eventi', as: 'h1', typo: 'displayXL' },
  ...props
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [events, setEvents] = useState<CalendarEvent[]>(props.events);
  const [isMobile, setIsMobile] = useState(useMediaQuery('(max-width: 1023px)'))
  const pageLocale = getPageLocale();
  const dateFnsLocale = {
    'it': it,
    'en': enGB
  }
  const locale = dateFnsLocale[pageLocale]
  const site = process?.env?.NEXT_PUBLIC_SITE_NAME || 'thesiadgroup-com'
  const isStorybook = process?.env?.NEXT_PUBLIC_IS_STORYBOOK === 'true'
  const t = useTranslations();
  
  // Funzione utility per ottenere la data di inizio di un evento
  const getEventStartDate = (event: CalendarEvent): Date => {
    if (event.dateFrom) {
      return parseISO(event.dateFrom);
    }
    return new Date(event.date);
  };
  
  // Funzione utility per ottenere la data di fine di un evento (se presente)
  const getEventEndDate = (event: CalendarEvent): Date | undefined => {
    if (event.dateTo) {
      return parseISO(event.dateTo);
    }
    return event.endDate;
  };

  useEffect(() => {
    if (!isMobile) setSelectedDate(new Date());
  }, [])

  useEffect(() => {
    if (selectedDate) {
      const events = getEventsForDate(selectedDate);
      setSelectedDateEvents(events);
      setShowSidebar(true);
    }
  }, [selectedDate]);
  
  // Generate days for the current month view
  const days = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    
    // Generate all days in the current month
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const getMonthNumber = (date: Date) => {
    return date.getMonth() + 1
  };

  const getYear = (date: Date) => {
    return date.getFullYear()
  };

  const getEvents = async (month: number, year: number) => {
    const apiUrl = API.getEvents(pageLocale, site, year, month)
    const data = await fetch(apiUrl)
    const res = await data.json()
    setEvents(res?.data)
  };
  
  // Navigate to previous month
  const prevMonth = async () => {
    const prevMonth = subMonths(currentDate, 1)
    setCurrentDate(prevMonth)
    const monthNumber = getMonthNumber(prevMonth)
    const year = getYear(prevMonth)
    await getEvents(monthNumber, year)
  };
  
  // Navigate to next month
  const nextMonth = async () => {
    const nextMonth = addMonths(currentDate, 1)
    setCurrentDate(nextMonth)
    const monthNumber = getMonthNumber(nextMonth)
    const year = getYear(nextMonth)
    await getEvents(monthNumber, year)
  };
  
  // Check if a date has events
  const dateHasEvents = (date: Date) => {
    // Prima controlla con il formato dateFrom/dateTo
    const hasFromToEvents = events.some(event => {
      if (event.dateFrom) {
        const startDate = parseISO(event.dateFrom);
        const endDate = event.dateTo ? parseISO(event.dateTo) : null;
        
        if (endDate) {
          // Per eventi multi-giorno
          return date >= startDate && date <= endDate;
        } else {
          // Per eventi di un solo giorno
          return isSameDay(startDate, date);
        }
      }
      return false;
    });

    // Poi controlla con il formato date/endDate
    const hasDateEvents = events.some(event => {
      if (event.date) {
        const startDate = new Date(event.date);
        const endDate = event.endDate ? new Date(event.endDate) : null;
        
        if (endDate) {
          // Per eventi multi-giorno
          return date >= startDate && date <= endDate;
        } else {
          // Per eventi di un solo giorno
          return isSameDay(startDate, date);
        }
      }
      return false;
    });

    return hasFromToEvents || hasDateEvents;
  };

  // Check if a date has multi-day events
  const dateHasMultiDayEvents = (date: Date) => {
    return events.some(event => {
      const startDate = getEventStartDate(event);
      const endDate = getEventEndDate(event);
      
      return endDate && (startDate <= date) && (endDate >= date);
    });
  };

  // Check if a date has single-day events
  const dateHasSingleDayEvents = (date: Date) => {
    return events.some(event => {
      const startDate = getEventStartDate(event);
      const endDate = getEventEndDate(event);
      
      return !endDate && isSameDay(startDate, date);
    });
  };
  
  // Count single-day events for a date
  const countSingleDayEvents = (date: Date) => {
    return events.filter(event => {
      const startDate = getEventStartDate(event);
      const endDate = getEventEndDate(event);
      
      return !endDate && isSameDay(startDate, date);
    }).length;
  };
  
  // Count multi-day events for a date
  const countMultiDayEvents = (date: Date) => {
    return events.filter(event => {
      const startDate = getEventStartDate(event);
      const endDate = getEventEndDate(event);
      
      return endDate && (startDate <= date) && (endDate >= date);
    }).length;
  };
  
  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const startDate = getEventStartDate(event);
      const endDate = getEventEndDate(event);
      
      // Se l'evento ha una data di fine, controlla se la data rientra nell'intervallo
      if (endDate) {
        return (startDate <= date) && (endDate >= date);
      }
      // Altrimenti controlla solo la data di inizio
      return isSameDay(startDate, date);
    });
  };
  
  // Handle day selection
  const handleDayClick = (day: Date) => {
    setSelectedDate(day);
    if (!showSidebar) {
      setShowSidebar(true);
    }
  };
  
  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };
  
  // Get formatted month and year
  const monthYearDisplay = format(currentDate, 'MMMM yyyy', { locale });
  
  // Get weekday names
  const weekdays = locale === it ? ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'] : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  return (
    <Container>
      { title ? (
        <>
          <Heading {...title} weight={'medium'} />
          <SpacerBase xs={3} xl={7} />
        </>
      ) : null }
      <S.CalendarContainer className="CalendarContainer">
        <S.CalendarGrid className="CalendarGrid">
          <S.MonthNavigation className="MonthNavigation">
            <S.NavButton className="NavButton" onClick={prevMonth} aria-label="Previous month">
              <Icon name="chevron-left" />
            </S.NavButton>
            <S.MonthTitle className="MonthTitle">
              <Heading 
                content={monthYearDisplay}
                typo={'headingMD'}
              />
            </S.MonthTitle>
            <S.NavButton className="NavButton" onClick={nextMonth} aria-label="Next month">
              <Icon name="chevron-right" />
            </S.NavButton>
          </S.MonthNavigation>
          
          <S.WeekdaysRow className="WeekdaysRow">
            {weekdays.map((weekday) => (
              <Heading 
                key={weekday}
                content={weekday}
                typo={'headingSM'}
              />
            ))}
          </S.WeekdaysRow>
          
          <S.DaysGrid className="DaysGrid">
            {Array(days[0].getDay() === 0 ? 6 : days[0].getDay() - 1)
              .fill(null)
              .map((_, index) => (
                <S.DayCell 
                  className="DayCell" 
                  key={`empty-${index}`} 
                  isCurrentMonth={false} 
                  empty
                />
              ))}
              
            {days.map((day) => (
              <S.DayCell 
                className="DayCell"
                key={day.toISOString()} 
                isCurrentMonth={true}
                isSelected={selectedDate ? isSameDay(day, selectedDate) : false}
                onClick={() => handleDayClick(day)}
                hasEvent={dateHasEvents(day)}
              >
                <S.DayNumber className="DayNumber" isToday={isToday(day)} isMobile={isMobile}>
                  <Paragraph 
                    typo={'headingSM'}
                    content={day.getDate().toString()}
                    weight={'medium'}
                  />
                </S.DayNumber>
                
                {/* Indicatori di eventi - logica semplificata */}
                <S.EventIndicatorWrapper>
                  {events.map((event, index) => {
                    // Controlla il formato dateFrom/dateTo
                    if (event.dateFrom) {
                      const startDate = parseISO(event.dateFrom);
                      const endDate = event.dateTo ? parseISO(event.dateTo) : null;
                      
                      if (endDate) {
                        // Evento multi-giorno
                        if (day >= startDate && day <= endDate) {
                          return (
                            <S.EventIndicator 
                              key={`multi-day-${event.id}-${index}`} 
                              className="EventIndicator" 
                              eventType="event" 
                            />
                          );
                        }
                      } else {
                        // Evento di un solo giorno
                        if (isSameDay(startDate, day)) {
                          return (
                            <S.EventIndicator 
                              key={`single-day-${event.id}-${index}`} 
                              className="EventIndicator" 
                              eventType="event" 
                            />
                          );
                        }
                      }
                    } 
                    // Controlla il formato date/endDate
                    else if (event.date) {
                      const startDate = new Date(event.date);
                      const endDate = event.endDate ? new Date(event.endDate) : null;
                      
                      if (endDate) {
                        // Evento multi-giorno
                        if (day >= startDate && day <= endDate) {
                          return (
                            <S.EventIndicator 
                              key={`multi-day-${event.id}-${index}`} 
                              className="EventIndicator" 
                              eventType="event" 
                            />
                          );
                        }
                      } else {
                        // Evento di un solo giorno
                        if (isSameDay(startDate, day)) {
                          return (
                            <S.EventIndicator 
                              key={`single-day-${event.id}-${index}`} 
                              className="EventIndicator" 
                              eventType="event" 
                            />
                          );
                        }
                      }
                    }
                    
                    return null;
                  })}
                </S.EventIndicatorWrapper>
              </S.DayCell>
            ))}
          </S.DaysGrid>
        </S.CalendarGrid>
        <S.EventSidebar 
          show={showSidebar && selectedDate}
          isMobile={isMobile}
          className="EventSidebar"
        >
          <S.EventHeader className="EventHeader">
            {selectedDate ? (
              <S.EventDate className="EventDate">
                <Heading 
                  content={format(selectedDate, 'd MMMM yyyy', { locale })}
                  typo={'headingMD'}
                />
              </S.EventDate>
            ) : null}
          </S.EventHeader>
          
          { showSidebar && selectedDate ? (
            <S.EventList className="EventList">
              <S.CollapseButton className="CollapseButton" onClick={toggleSidebar} aria-label="Close sidebar">
                <Button 
                  label={t('calendar.backLabel')}
                  variant="secondary"
                  size="small"
                  iconStart="chevron-left"
                />
              </S.CollapseButton>
              <S.EventListInner className="EventListInner">
                <S.EventListScrollBar className="EventListScrollBar">
                  {selectedDateEvents.length > 0 ? (
                    selectedDateEvents.map((event) => (
                      <AccordionSub
                        id={event.id}
                        variant="event"
                        data={event}
                      />
                    ))
                  ) : (
                    <S.EventItem className="EventItem">{t('calendar.noEventsLabel')}</S.EventItem>
                  )}
                </S.EventListScrollBar>
              </S.EventListInner>
            </S.EventList>
          ) : null}
        </S.EventSidebar>
      </S.CalendarContainer>
    </Container>
  );
};

export default Calendar;